import { Role } from './user';

/**
 * League Tenant Permissions Matrix (v2, 11 June 2026) — capability taxonomy.
 *
 * Stable capability identifiers grouped by matrix area. This is the single
 * source of truth shared by the API (server-side enforcement via `can()` /
 * `@RequireCapability`) and the frontends (capability-derived nav / UI gating).
 * Each identifier maps 1:1 to a row in the source matrix.
 */
export const Capability = {
  // Competition
  CompetitionFixturesView: 'competition.fixtures.view',
  CompetitionTablesView: 'competition.tables.view',
  CompetitionTablesEdit: 'competition.tables.edit',
  CompetitionFriendliesManage: 'competition.friendlies.manage',
  CompetitionCupManage: 'competition.cup.manage',
  CompetitionCupEdit: 'competition.cup.edit',
  CompetitionCupDateApprove: 'competition.cupDate.approve',

  // Clubs & players
  ClubsKeyFieldsView: 'clubs.keyFields.view',
  ClubsKeyFieldsEdit: 'clubs.keyFields.edit',
  ClubsSupplementaryEdit: 'clubs.supplementary.edit',
  ClubsDetailChangeApprove: 'clubs.detailChange.approve',
  PlayersCoreView: 'players.core.view',
  PlayersEnrichmentEdit: 'players.enrichment.edit',

  // News & media
  NewsArticlesCreate: 'news.articles.create',
  NewsArticlesEditAny: 'news.articles.editAny',
  NewsArticlesDelete: 'news.articles.delete',
  NewsFeatured: 'news.featured',
  TagsManage: 'tags.manage',
  MediaUpload: 'media.upload',
  MediaDelete: 'media.delete',
  SocialManage: 'social.manage',
  VideoCurate: 'video.curate',
  VideoChannels: 'video.channels',
  FaqsManage: 'faqs.manage',
  MediaViewPrivate: 'media.viewPrivate',

  // Documents
  DocsContentsView: 'docs.contents.view',
  DocsContentsManage: 'docs.contents.manage',
  DocsPermissionsManage: 'docs.permissions.manage',
  DocsDelete: 'docs.delete',

  // Site
  SiteNavigation: 'site.navigation',
  EventsManage: 'events.manage',
  SiteStaff: 'site.staff',
  SitePages: 'site.pages',
  SiteSponsors: 'site.sponsors',
  SiteSettings: 'site.settings',

  // Users & oversight
  UsersManage: 'users.manage',
  UsersGuestsManage: 'users.guests.manage',
  AuditView: 'audit.view',

  // Engagement
  EngagementsManage: 'engagements.manage',
} as const;
export type Capability = (typeof Capability)[keyof typeof Capability];

/** Every capability identifier in the matrix (League Moderator / SuperAdmin hold all). */
export const ALL_CAPABILITIES: Capability[] = Object.values(Capability);

/**
 * League Admin — competition operations + supplementary club data + player
 * enrichment, plus the two approver capabilities (cup-date & club-detail
 * changes), plus read-only File Manager access (browse/search/view/download
 * league-owned files and folders — no upload, rename, move or delete). Does
 * NOT include direct key-field editing (League Moderator only).
 */
const LEAGUE_ADMIN_CAPABILITIES: Capability[] = [
  Capability.CompetitionFixturesView,
  Capability.CompetitionTablesView,
  Capability.CompetitionTablesEdit,
  Capability.CompetitionFriendliesManage,
  Capability.CompetitionCupManage,
  Capability.CompetitionCupEdit,
  Capability.CompetitionCupDateApprove,
  Capability.ClubsKeyFieldsView,
  Capability.ClubsSupplementaryEdit,
  Capability.ClubsDetailChangeApprove,
  Capability.PlayersCoreView,
  Capability.PlayersEnrichmentEdit,
  Capability.DocsContentsView,
  Capability.EventsManage,
];

/** Media Manager — the full news & media area. */
const MEDIA_MANAGER_CAPABILITIES: Capability[] = [
  Capability.NewsArticlesCreate,
  Capability.NewsArticlesEditAny,
  Capability.NewsArticlesDelete,
  Capability.NewsFeatured,
  Capability.TagsManage,
  Capability.MediaUpload,
  Capability.MediaDelete,
  Capability.SocialManage,
  Capability.VideoCurate,
  Capability.VideoChannels,
  Capability.FaqsManage,
  Capability.MediaViewPrivate,
];

/** League Director — the documents area only. */
const LEAGUE_DIRECTOR_CAPABILITIES: Capability[] = [
  Capability.DocsContentsView,
  Capability.DocsContentsManage,
  Capability.DocsPermissionsManage,
  Capability.DocsDelete,
];

/**
 * League Curator — the union of League Admin + Media Manager. Derived from those
 * two bundles (never hand-maintained), so it automatically picks up any
 * capability added to either parent role.
 */
const LEAGUE_CURATOR_CAPABILITIES: Capability[] = Array.from(
  new Set<Capability>([...LEAGUE_ADMIN_CAPABILITIES, ...MEDIA_MANAGER_CAPABILITIES])
);

/**
 * Role → capability bundle map. SuperAdmin and the full-access admin role (the
 * `Admin` value, displayed as "League Moderator" on league tenants) hold every
 * tenant capability. `ClubAdmin` carries no v2 capabilities — its access is
 * governed by the separate `access.clubs.update` record-scoping plane, which
 * this epic leaves untouched. `Moderator` is dissolved by RBAC v2 (migrated to
 * `MediaManager`) and resolves to no capabilities.
 */
/**
 * Roles that may request the fixture-data anomaly report for a league.
 *
 * Shared rather than written out at each gate: the admin button and the API's `@Auth` decorator are
 * the same rule in two apps, and keeping two hand-maintained copies is how `Role.Admin` — the
 * full-access tenant role, labelled "League Moderator" on a league tenant — ended up in neither,
 * locking out every user who actually runs a league while the narrower `LeagueAdmin` was let
 * through.
 *
 * `Role.Admin` is not implied by anything here. `AuthGuard` bypasses the list for `SuperAdmin`
 * only, and this endpoint gates on roles rather than capabilities, so a role holding
 * `ALL_CAPABILITIES` still has to be named. Migrating it to `@RequireCapability` would remove the
 * list altogether, but no capability models "send the anomaly report" and the nearest fit
 * (`CompetitionFixturesView`) would silently widen access to `LeagueCurator` — a product decision,
 * not a refactor.
 */
export const ANOMALY_REPORT_ROLES: Role[] = [Role.Admin, Role.LeagueAdmin, Role.SuperAdmin];

/** Roles that may pull a match's current data from IFAS by hand and read the pull history. */
export const IFAS_MANUAL_PULL_ROLES: Role[] = [Role.Admin, Role.LeagueAdmin, Role.SuperAdmin];

export const ROLE_CAPABILITIES: Record<Role, Capability[]> = {
  [Role.SuperAdmin]: ALL_CAPABILITIES,
  [Role.Admin]: ALL_CAPABILITIES,
  [Role.LeagueAdmin]: LEAGUE_ADMIN_CAPABILITIES,
  [Role.MediaManager]: MEDIA_MANAGER_CAPABILITIES,
  [Role.LeagueDirector]: LEAGUE_DIRECTOR_CAPABILITIES,
  [Role.LeagueCurator]: LEAGUE_CURATOR_CAPABILITIES,
  [Role.ClubAdmin]: [],
  [Role.ClubOfficer]: [],
  [Role.User]: [],
  [Role.Anonymous]: [],
};
